import { Box, Typography, makeStyles, Theme } from "@material-ui/core";
import {
  CheckCircle,
  Error as ErrorIcon,
  ArrowForward,
} from "@material-ui/icons";
import { SignalRMessage, SignalRStatusEnum } from "./types";
import { useImportData } from "../../../context/ImportDataContext";

type Props = {
  message: SignalRMessage;
  onActionClick?: () => void;
};

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  successIcon: {
    color: theme.palette.success.main,
  },
  errorIcon: {
    color: theme.palette.error.main,
  },
  divider: {
    borderTop: `1px solid ${theme.palette.divider}`,
    paddingTop: theme.spacing(1.5),
    marginTop: theme.spacing(1),
  },
  content: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  actionLink: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    color: theme.palette.primary.main,
    cursor: "pointer",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  timestamp: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
}));

const ImportSignalRMessage = ({ message, onActionClick }: Props) => {
  const classes = useStyles();
  const { openImportForm } = useImportData();

  const handleStartImport = () => {
    if (message.importId && message.machineName) {
      openImportForm(message.machineName, message.importId);
      onActionClick?.();
    }
  };

  const getTitle = () => {
    switch (message.status) {
      case SignalRStatusEnum.Success:
        return "Import Complete";
      case SignalRStatusEnum.Error:
        return "Import Failed";
      case SignalRStatusEnum.Neutral:
        return "Import In Progress";
      default:
        return "Import";
    }
  };

  const getIcon = () => {
    switch (message.status) {
      case SignalRStatusEnum.Success:
        return <CheckCircle className={classes.successIcon} />;
      case SignalRStatusEnum.Error:
        return <ErrorIcon className={classes.errorIcon} />;
      default:
        return null;
    }
  };

  return (
    <Box className={classes.container}>
      <Box className={classes.header}>
        {getIcon()}
        <Typography variant="body1" noWrap style={{ maxWidth: 250 }}>
          {getTitle()}
        </Typography>
      </Box>

      <Box className={`${classes.content} ${classes.divider}`}>
        {message.status === SignalRStatusEnum.Success && message.importId ? (
          <>
            <Typography variant="body2">Import is ready.</Typography>
            <div className={classes.actionLink} onClick={handleStartImport}>
              <ArrowForward fontSize="small" />
              <span>Start import process</span>
            </div>
          </>
        ) : (
          <>
            <Typography variant="body2">Something went wrong.</Typography>
            <Typography variant="body2">Try importing again.</Typography>
          </>
        )}

        <Typography className={classes.timestamp}>
          {new Date(message.timestamp).toLocaleTimeString()}
        </Typography>
      </Box>
    </Box>
  );
};

export default ImportSignalRMessage;
